import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { MapPin, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, setUserToken, setStoredUser } from "@/lib/api";

export default function Login({ mode = "user" }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authCfg, setAuthCfg] = useState({ google_provider: "emergent", own_google_enabled: false });

  const isAdmin = mode === "admin";
  const successPath = isAdmin ? "/admin" : "/app";

  useEffect(() => {
    axios.get(`${API}/auth/config`).then((r) => setAuthCfg(r.data)).catch(() => {});
  }, []);

  const emailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login-email`,
        { email, password },
        { withCredentials: true }
      );
      setUserToken(data.session_token);
      setStoredUser(data.user);
      const role = data.user.role;
      if (isAdmin && !["owner", "editor", "viewer"].includes(role)) {
        toast.error("This account has no admin access");
        setLoading(false);
        return;
      }
      toast.success(`Welcome back, ${data.user.name}`);
      nav(successPath, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    // Guard: only allow when own Google OAuth is configured. Emergent fallback
    // is disabled because it shows Emergent branding at the login step, which
    // is not desired for a shipping product.
    if (!authCfg.own_google_enabled) {
      toast.error("Google sign-in is not configured yet. Ask an admin to add credentials at /admin/secrets.");
      return;
    }
    window.location.href = `${API}/auth/google/start?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const EmailPane = (
    <div className="pt-5">
      {authCfg.own_google_enabled ? (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={googleLogin}
            data-testid="google-login-btn"
            className="w-full h-11 mb-4 font-semibold"
          >
            <ShieldCheck className="w-4 h-4 mr-2 text-primary" />
            Continue with Google
          </Button>
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-card px-2 text-xs uppercase tracking-widest text-muted-foreground">
                or with email
              </span>
            </div>
          </div>
        </>
      ) : null}
      <form onSubmit={emailSubmit} className="space-y-4">
        <div>
          <Label htmlFor="e" className="text-sm font-semibold">Email</Label>
          <Input
            id="e"
            type="email"
            data-testid="login-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="mt-1.5 h-11"
            autoComplete="email"
          />
        </div>
        <div>
          <Label htmlFor="p" className="text-sm font-semibold">Password</Label>
          <Input
            id="p"
            type="password"
            data-testid="login-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            className="mt-1.5 h-11"
            autoComplete="current-password"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="w-full h-11 font-semibold group"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Sign in
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </Button>
        {!isAdmin && (
          <p className="text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              data-testid="link-register"
              className="text-primary font-semibold hover:underline"
            >
              Sign up
            </Link>
          </p>
        )}
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-grid flex items-center justify-center p-6">
      <div
        data-testid="login-card"
        className="w-full max-w-md bg-card rounded-2xl shadow-[0_10px_40px_-10px_rgba(255,107,0,0.15)] border border-border p-8 animate-in-up"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
            <MapPin className="w-6 h-6 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              NearMe
            </div>
            <div className="text-lg font-bold leading-tight">
              {isAdmin ? "Admin Console" : "Sign in"}
            </div>
          </div>
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight mb-1">
          {isAdmin ? "Admin access" : "Welcome back"}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          {isAdmin
            ? "Sign in with your admin (owner / editor / viewer) account."
            : "Sign in to your NearMe account."}
        </p>

        {EmailPane}
      </div>
    </div>
  );
}
